/* eslint-disable max-params, max-statements, no-mixed-operators, @typescript-eslint/no-inferrable-types */
import { Vector3 } from 'three';

import { generateQuadraticBezierPoints } from '../animation/path-interpolation';

/**
 * Configuration for highlight circle
 */
export interface HighlightCircleConfig {
	/** Center X position */
	centerX: number;
	/** Center Z position */
	centerZ: number;
	/** Y position (height) of the circle */
	y: number;
	/** Radius of the circle */
	radius: number;
}

/**
 * Calculate the intersection point of a ray with a circle in the XZ plane
 *
 * @param rayStart Ray start point
 * @param rayDirection Ray direction (should be normalized or at least have non-zero XZ)
 * @param circle Circle configuration
 * @returns Intersection point or null if no intersection
 */
export const calculateRayCircleIntersection = (
	rayStart: Vector3,
	rayDirection: Vector3,
	circle: HighlightCircleConfig
): Vector3 | null => {
	const { centerX, centerZ, y, radius } = circle;

	// Ray parameters (in XZ plane)
	const rayStartX = rayStart.x;
	const rayStartZ = rayStart.z;
	const rayDirX = rayDirection.x;
	const rayDirZ = rayDirection.z;

	// Solve ray-circle intersection:
	// Circle equation: (x - cx)² + (z - cz)² = r²
	// Ray: x = rayStartX + t * rayDirX, z = rayStartZ + t * rayDirZ
	const dx = rayStartX - centerX;
	const dz = rayStartZ - centerZ;

	const a = rayDirX * rayDirX + rayDirZ * rayDirZ;
	const b = 2 * (rayDirX * dx + rayDirZ * dz);
	const c = dx * dx + dz * dz - radius * radius;

	const discriminant = b * b - 4 * a * c;

	if (discriminant < 0 || a < 0.0001) {
		return null;
	}

	const sqrtDisc = Math.sqrt(discriminant);
	const t1 = (-b + sqrtDisc) / (2 * a);
	const t2 = (-b - sqrtDisc) / (2 * a);

	// We want the intersection in the forward direction (positive t)
	let t = 0;
	if (t1 > 0 && t2 > 0) {
		t = Math.max(t1, t2); // Take the further exit point
	} else if (t1 > 0) {
		t = t1;
	} else if (t2 > 0) {
		t = t2;
	}

	if (t > 0) {
		return new Vector3(
			rayStartX + rayDirX * t,
			y,
			rayStartZ + rayDirZ * t
		);
	}

	return null;
};

/**
 * Calculate highlight light source position on the highlight circle
 *
 * The source is positioned perpendicular to the front face of the object,
 * found by casting a ray from the front face center in the direction of
 * the front face normal.
 *
 * @param frontFacePoint Center of the front face
 * @param frontFaceNormal Normal vector of the front face
 * @param circle Highlight circle configuration
 * @returns Position on the circle perpendicular to the front face
 */
export const calculateHighlightLightSource = (
	frontFacePoint: Vector3,
	frontFaceNormal: Vector3,
	circle: HighlightCircleConfig
): Vector3 => {
	// Try ray-circle intersection first
	const intersection = calculateRayCircleIntersection(frontFacePoint, frontFaceNormal, circle);

	if (intersection) {
		return intersection;
	}

	// Fallback: place on circle in direction of front face normal from center
	const { centerX, centerZ, y, radius } = circle;
	return new Vector3(
		centerX + frontFaceNormal.x * radius,
		y,
		centerZ + frontFaceNormal.z * radius
	);
};

/**
 * Calculate the midpoint on a circle between two points
 *
 * Projects the midpoint onto the circle at the specified radius
 *
 * @param point1 First point (should be on or near the circle)
 * @param point2 Second point (should be on or near the circle)
 * @param circle Circle configuration
 * @returns Midpoint projected onto the circle
 */
export const calculateCircleMidpoint = (
	point1: Vector3,
	point2: Vector3,
	circle: HighlightCircleConfig
): Vector3 => {
	const { centerX, centerZ, y, radius } = circle;

	const midX = (point1.x + point2.x) / 2;
	const midZ = (point1.z + point2.z) / 2;

	// Vector from center to midpoint
	const midToCenterX = midX - centerX;
	const midToCenterZ = midZ - centerZ;
	const midDistFromCenter = Math.sqrt(midToCenterX * midToCenterX + midToCenterZ * midToCenterZ);

	if (midDistFromCenter > 0.001) {
		// Normalize and scale to circle radius
		return new Vector3(
			centerX + (midToCenterX / midDistFromCenter) * radius,
			y,
			centerZ + (midToCenterZ / midDistFromCenter) * radius
		);
	}

	// If points are opposite on circle, pick perpendicular direction
	const perpX = -(point1.z - centerZ);
	const perpZ = point1.x - centerX;
	const perpLen = Math.sqrt(perpX * perpX + perpZ * perpZ);

	return new Vector3(
		centerX + (perpX / perpLen) * radius,
		y,
		centerZ + (perpZ / perpLen) * radius
	);
};

/**
 * Calculate transition arc points between two highlight targets
 *
 * Creates a quadratic bezier curve from the previous target through a
 * midpoint on the highlight circle to the new target.
 *
 * @param previousTarget Previous highlight target position
 * @param previousSource Previous highlight light source position
 * @param newTarget New highlight target position
 * @param newSource New highlight light source position
 * @param circle Highlight circle configuration
 * @param segments Number of segments for the arc (default: 32)
 * @returns Object containing arc points and the arc midpoint
 */
export const calculateTransitionArc = (
	previousTarget: Vector3,
	previousSource: Vector3,
	newTarget: Vector3,
	newSource: Vector3,
	circle: HighlightCircleConfig,
	segments = 32
): { points: Vector3[]; midpoint: Vector3 } => {
	// Calculate arc midpoint on the circle (between the two sources)
	const arcMidpoint = calculateCircleMidpoint(previousSource, newSource, circle);

	// Generate bezier curve points
	const points = generateQuadraticBezierPoints(previousTarget, arcMidpoint, newTarget, segments);

	return { points, midpoint: arcMidpoint };
};

